import { PREMISSIONS } from '../enums/permissions';

/**
 * Static mapping: API resourceName/className -> app path and icon key (ICON_MAP).
 * Paths are defined in the app; API path is not used for routing.
 *
 * Reference: document/proplem.md (my-modules API response).
 * Module codes: FIN, INV, PRC, CRM, POS, RPT, ZAT, USR, MNS, CCD, SET.
 * Every resourceName returned by the API must have an entry here for sidebar + guard.
 */
export const DEFAULT_SIDEBAR_ICON = 'app-settings-icon';

export interface SidebarPageMapping {
  path: string;
  createPath?: string;
  createPermissionKey?: string;
  icon: string;
  /** Extra URL prefixes for breadcrumb/route resolution when real routes differ from `path` (e.g. singular create URL). */
  breadcrumbPaths?: readonly string[];
}

/** Page (resourceName) -> app path and icon. Used for sidebar menu items from my-modules API. */
export const SIDEBAR_PAGE_MAPPING: Record<string, SidebarPageMapping> = {
  // CRM / Sales
  Customer: { path: '/sales/customers', createPath: '/sales/customers/create-customer/create/new', createPermissionKey: PREMISSIONS.Customer?.Create, icon: 'app-sale-icon' },
  Sales: { path: '/sales/sales-invoices', createPath: '/sales/sales-invoices/create-invoice/create/new', createPermissionKey: PREMISSIONS.Sales?.Create, icon: 'app-sale-icon' },
  SalesOrder: { path: '/sales/sales-orders', createPath: '/sales/sales-orders/create-sales-order/create/new', createPermissionKey: PREMISSIONS.SalesOrders?.Create, icon: 'app-sale-icon' },
  DeliveryNote: { path: '/sales/delivery-note', createPath: '/sales/delivery-note/create-delivery-note/create/new', createPermissionKey: PREMISSIONS.DeliveryNotes?.Create, icon: 'app-sale-icon' },
  SalesReturn: { path: '/sales/sales-return', icon: 'app-sale-icon' },
  OfferPrice: { path: '/sales/quotations', createPath: '/sales/quotations/create-quotation/create/new', createPermissionKey: PREMISSIONS.OfferPrice?.Create, icon: 'app-sale-icon' },
  CustomerRelations: { path: '/sales/customer-relations', icon: 'app-sale-icon' },
  // POS
  Shift: { path: '/shifts', createPath: '/shifts/create-update-shift/create/new', createPermissionKey: PREMISSIONS.Shift?.Create, icon: 'app-stores-clock' },
  ShiftCategory: { path: '/category-shift', createPath: '/category-shift/create-update-shift-category/create/new', createPermissionKey: PREMISSIONS.ShiftCategory?.Create, icon: 'app-stores-clock' },
  POSTerminals: { path: '/pos-invoices', icon: 'app-stores-clock' },
  POSSales: { path: '/pos-invoices', createPath: '/pos-invoices/create-invoice/create/new', createPermissionKey: PREMISSIONS.POSTerminals?.Create, icon: 'app-stores-clock' },
  POSReturn: { path: '/pos-invoices', icon: 'app-stores-clock' },
  POSReports: { path: '/pos-invoices', icon: 'app-stores-clock' },
  // ZATCA (list: zatcaUnits; create/edit: zatcaUnit/:action/:id)
  ZatcaUnit: {
    path: '/zatca/zatcaUnits',
    createPath: '/zatca/zatcaUnit/create/new',
    createPermissionKey: PREMISSIONS.ZatcaUnits?.Create,
    icon: 'app-doller-icon',
    breadcrumbPaths: ['/zatca/zatcaUnit'],
  },
  DocumentZatca: { path: '/zatca/zatca-invoices-list', icon: 'app-doller-icon' },
  // Users & Roles
  Users: { path: '/users/list', createPath: '/users/list/create-user/create/new', createPermissionKey: PREMISSIONS.Users?.Create, icon: 'app-users-icon1' },
  UserRoles: { path: '/users/list', createPath: '/users/list/create-user/create/new', createPermissionKey: PREMISSIONS.UserRoles?.Create, icon: 'app-users-icon1' },
  UserClaims: { path: '/user-claims', icon: 'app-users-icon1' },
  Roles: { path: '/roles', createPath: '/roles/create-update-role/create/new', createPermissionKey: PREMISSIONS.Roles?.Create, icon: 'app-users-icon1' },
  RoleClaims: { path: '/roles-claims', icon: 'app-users-icon1' },
  // Messages & Notifications
  MessageLog: {
    path: '/sms-management/sms-messages',
    createPath: '/sms-management/create-sms-message/create/new/null',
    createPermissionKey: PREMISSIONS.SMSMessage?.Create,
    icon: 'app-sms-messages',
    breadcrumbPaths: ['/sms-management/create-sms-message'],
  },
  EmailMessage: { path: '/sms-management/create-sms-message/create/new', icon: 'app-sms-messages' },
  WhatsappMessage: { path: '/sms-management/create-sms-message/create/new', icon: 'app-sms-messages' },
  SMSMessage: { path: '/sms-management/create-sms-message/create/new', icon: 'app-sms-messages' },
  MessageTemplate: {
    path: '/sms-management/list-sms-templates',
    createPath: '/sms-management/create-sms-template/create/new',
    createPermissionKey: PREMISSIONS.MessageTemplate?.Create,
    icon: 'app-sms-messages',
    breadcrumbPaths: ['/sms-management/create-sms-template'],
  },
  ScheduleTemplate: { path: '/sms-management/list-sms-templates', icon: 'app-sms-messages' },
  // Settings
  Company: { path: '/settings/currentCompany/edit/current', icon: 'app-settings-icon' },
  Branch: { path: '/settings/branch/', icon: 'app-settings-icon' },
  Currencies: {
    path: '/settings/currencies',
    createPath: '/settings/currency/create/new',
    createPermissionKey: PREMISSIONS.Currencies?.Create,
    icon: 'app-settings-icon',
    breadcrumbPaths: ['/settings/currency'],
  },
  IntermediateAccount: { path: '/settings/intermediate-accounts', icon: 'app-settings-icon' },
  TaxCategory: {
    path: '/settings/taxCategories',
    createPath: '/settings/taxCategory/create/new',
    createPermissionKey: PREMISSIONS.TaxCategory?.Create,
    icon: 'app-settings-icon',
    breadcrumbPaths: ['/settings/taxCategory'],
  },
  Payment: { path: '/payments', icon: 'app-settings-icon' },
  GeneralSettings: { path: '/settings/general-settings', icon: 'app-settings-icon' },
  Attachments: { path: '/attachments', icon: 'app-settings-icon' },
  SubscriptionManagement: { path: '/subscription-management', icon: 'app-subscription-icon' },
  Closing: {
    path: '/accounting/closing-management',
    icon: 'app-settings-icon',
    breadcrumbPaths: [
      '/accounting/closing-management/add-closing',
      '/accounting/closing-management/list-closing',
      '/accounting/closing-management/view-closing',
      '/accounting/closing-management/print-closing',
    ],
  },
  // Accounting (if returned by API)
  // GLAccount: no createPath intentionally — the create form requires a parent account context
  // that is only available from within the chart-of-accounts tree. Direct sidebar navigation is not supported.
  GLAccount: { path: '/accounting/accounts', icon: 'app-doller-icon' },
  Journal: { path: '/accounting/journals', createPath: '/accounting/journals/create-or-update-journal/create/new', createPermissionKey: PREMISSIONS.Journal?.Create, icon: 'app-doller-icon' },
  ReceiptVoucher: { path: '/accounting/receipt-vouchers', createPath: '/accounting/receipt-vouchers/create-or-update-receipt-voucher/create/new', createPermissionKey: PREMISSIONS.ReceiptVoucher?.Create, icon: 'app-doller-icon' },
  PaymentVoucher: { path: '/accounting/payment-vouchers', createPath: '/accounting/payment-vouchers/create-or-update-payment-voucher/create/new', createPermissionKey: PREMISSIONS.PaymentVoucher?.Create, icon: 'app-doller-icon' },
  FinancialYear: { path: '/fiscal-years', createPath: '/fiscal-years/create-update-fiscal-year/create/new', createPermissionKey: PREMISSIONS.FinancialYear?.Create, icon: 'app-doller-icon' },
  FinancialStatementItem: { path: '/accounting/financial-statements', icon: 'app-doller-icon' },
  GlAccountType: { path: '/accounting/account-types', icon: 'app-doller-icon' },
  GlAccountCategory: { path: '/accounting/accountCategory', createPath: '/accounting/accountCategory/create-or-update-account-category/create/new', createPermissionKey: PREMISSIONS.GlAccountCategory?.Create, icon: 'app-doller-icon' },
  JournalTypes: { path: '/accounting/journal-types', createPath: '/accounting/journal-types/create-or-update-journal-type/create/new', createPermissionKey: PREMISSIONS.JournalTypes?.Create, icon: 'app-doller-icon' },
  OpeningBalance: { path: '/open-balance', createPath: '/open-balance/create-open-balance/create/new', createPermissionKey: (PREMISSIONS.OpeningBalance as any)?.UpSert, icon: 'app-doller-icon' },
  // Purchases (API uses Purches / PurchesReturn)
  Vendor: { path: '/purchases/vendors', createPath: '/purchases/vendors/create-vendor/create/new', createPermissionKey: PREMISSIONS.Vendor?.Create, icon: 'app-purchase-icon' },
  Purches: { path: '/purchases/purchases-invoices', createPath: '/purchases/purchases-invoices/create-invoice/create/new', createPermissionKey: PREMISSIONS.Purches?.Create, icon: 'app-purchase-icon' },
  PurchesReturn: { path: '/purchases/purchases-return', icon: 'app-purchase-icon' },
  PurchaseRequisitions: { path: '/purchases/purchase-requisitions', createPath: '/purchases/purchase-requisitions/create-purchase-requisition/create/new', createPermissionKey: PREMISSIONS.PurchaseRequisitions?.Create, icon: 'app-purchase-icon' },
  PurchaseOrders: { path: '/purchases/purchase-orders', createPath: '/purchases/purchase-orders/create-purchase-order/create/new', createPermissionKey: PREMISSIONS.PurchaseOrders?.Create, icon: 'app-purchase-icon' },
  GoodsReceipts: { path: '/purchases/goods-receipts', createPath: '/purchases/goods-receipts/create-goods-receipt/create/new', createPermissionKey: PREMISSIONS.GoodsReceipts?.Create, icon: 'app-purchase-icon' },
  // Stock / Inventory (API: WarehouseTransfer, BranchTransfer, InventoryDisbursement, InventorySupply)
  Products: { path: '/stock/products/', createPath: '/stock/products/create-or-update-product/create/new', createPermissionKey: PREMISSIONS.Products?.Create, icon: 'app-stores-icon' },
  ProductGroup: { path: '/stock/product-categories', createPath: '/stock/product-categories/create-update-product-category/create/new', createPermissionKey: PREMISSIONS.ProductGroup?.Create, icon: 'app-stores-icon' },
  Unit: { path: '/stock/product-units', createPath: '/stock/product-units/create-update-unit/create/new', createPermissionKey: PREMISSIONS.Unit?.Create, icon: 'app-stores-icon' },
  Store: { path: '/stock/stores', createPath: '/stock/stores/create-update-store/create/new', createPermissionKey: PREMISSIONS.Store?.Create, icon: 'app-stores-icon' },
  ProductBarcode: { path: '/stock/products-barcode', icon: 'app-stores-icon' },
  ManualInventory: { path: '/manual-inventory', createPath: '/manual-inventory/create-manaul-inventory/create/new', createPermissionKey: PREMISSIONS.ManualInventory?.Create, icon: 'app-stores-icon' },
  OpeningStock: { path: '/opening-stock', createPath: '/opening-stock/create-opening-stock/create/new', createPermissionKey: PREMISSIONS.OpeningStock?.Create, icon: 'app-stores-icon' },
  BranchesTransfer: { path: '/stock/branches-transfer', createPath: '/stock/branches-transfer/create-branches-transfer/create/new', createPermissionKey: PREMISSIONS.BranchesTransfer?.Create, icon: 'app-stores-icon' },
  BranchTransfer: { path: '/stock/branches-transfer', createPath: '/stock/branches-transfer/create-branches-transfer/create/new', createPermissionKey: PREMISSIONS.BranchesTransfer?.Create, icon: 'app-stores-icon' },
  WarehousesTransfer: { path: '/stock/warehouses-transfer', createPath: '/stock/warehouses-transfer/create-warehouses-transfer/create/new', createPermissionKey: PREMISSIONS.WarehousesTransfer?.Create, icon: 'app-stores-icon' },
  WarehouseTransfer: { path: '/stock/warehouses-transfer', createPath: '/stock/warehouses-transfer/create-warehouses-transfer/create/new', createPermissionKey: PREMISSIONS.WarehousesTransfer?.Create, icon: 'app-stores-icon' },
  WarehouseIssueOrder: { path: '/stock/warehouse-issue-order', createPath: '/stock/warehouse-issue-order/create-warehouse-issue-order/create/new', createPermissionKey: PREMISSIONS.WarehouseIssueOrder?.Create, icon: 'app-stores-icon' },
  InventoryDisbursement: { path: '/stock/warehouse-issue-order', createPath: '/stock/warehouse-issue-order/create-warehouse-issue-order/create/new', createPermissionKey: PREMISSIONS.WarehouseIssueOrder?.Create, icon: 'app-stores-icon' },
  WarehouseSupplyOrder: { path: '/stock/warehouse-supply-order', createPath: '/stock/warehouse-supply-order/create-warehouse-supply-order/create/new', createPermissionKey: PREMISSIONS.WarehouseSupplyOrder?.Create, icon: 'app-stores-icon' },
  InventorySupply: { path: '/stock/warehouse-supply-order', createPath: '/stock/warehouse-supply-order/create-warehouse-supply-order/create/new', createPermissionKey: PREMISSIONS.WarehouseSupplyOrder?.Create, icon: 'app-stores-icon' },
  // Reports
  AccountStatement: { path: '/report/simple-account-statement', icon: 'app-report-icon' },
  CustomerAccountStatement: { path: '/report/customers-account-statement', icon: 'app-report-icon' },
  SupplierAccountStatement: { path: '/report/vendors-account-statement', icon: 'app-report-icon' },
  TaxReturn: { path: '/report/tax-return', icon: 'app-report-icon' },
  TrialBalance: { path: '/report/trail-balance', icon: 'app-report-icon' },
  StockMovement: { path: '/stock/stock-movement', icon: 'app-stores-icon' },
  InventoryMovement: { path: '/report/stock-movement', icon: 'app-report-icon' },
  ActualInventory: { path: '/report/actual-inventory', icon: 'app-report-icon' },
  InvoicesReport: { path: '/report/invoice-reports', icon: 'app-report-icon' },
  CustomerInvoicesReport: { path: '/report/customers-invoice-reports', icon: 'app-report-icon' },
  SupplierInvoicesReport: { path: '/report/vendors-invoice-reports', icon: 'app-report-icon' },
};

/** Module code (from API) -> icon key for the module group header. */
export const SIDEBAR_MODULE_ICON_MAPPING: Record<string, string> = {
  CRM: 'app-sale-icon',
  PRC: 'app-purchase-icon',
  INV: 'app-stores-icon',
  FIN: 'app-doller-icon',
  POS: 'app-stores-clock',
  RPT: 'app-report-icon',
  ZAT: 'app-zakat-icon',
  USR: 'app-users-icon1',
  MNS: 'app-sms-messages',
  SET: 'app-settings-icon',
  CCD: 'app-subscription-icon',
};

export function getPageMapping(resourceName: string): SidebarPageMapping {
  return (
    SIDEBAR_PAGE_MAPPING[resourceName] ?? {
      path: '',
      icon: DEFAULT_SIDEBAR_ICON,
    }
  );
}

export function getModuleIcon(moduleCode: string): string {
  const code = String(moduleCode ?? '').toUpperCase();
  return SIDEBAR_MODULE_ICON_MAPPING[code] ?? DEFAULT_SIDEBAR_ICON;
}

/*
  Module (code) -> resourceNames from API (proplem.md). All must exist in SIDEBAR_PAGE_MAPPING above.
  FIN: GLAccount, Journal, ReceiptVoucher, PaymentVoucher, FinancialStatementItem, FinancialYear, GlAccountType, GlAccountCategory, JournalTypes, OpeningBalance, Closing
  INV: Products, StockMovement, ProductGroup, Unit, ProductBarcode, Store, OpeningStock, ManualInventory, WarehouseTransfer, BranchTransfer, InventoryDisbursement, InventorySupply, FinancialYear
  PRC: Vendor, Purches, PurchesReturn, PurchaseRequisitions, PurchaseOrders
  CRM: Customer, Sales, SalesReturn, OfferPrice, CustomerRelations
  POS: Shift, ShiftCategory, POSTerminals, POSSales, POSReturn, POSReports
  RPT: AccountStatement, CustomerAccountStatement, SupplierAccountStatement, TaxReturn, TrialBalance, InventoryMovement, ActualInventory, InvoicesReport, CustomerInvoicesReport, SupplierInvoicesReport
  ZAT: ZatcaUnit, DocumentZatca
  USR: Users, UserRoles, UserClaims, Roles, RoleClaims
  MNS: EmailMessage, WhatsappMessage, SMSMessage, MessageTemplate, ScheduleTemplate
  CCD: Payment, SubscriptionManagement
  SET: Company, Branch, Currencies, IntermediateAccount, TaxCategory, GeneralSettings, Attachments, SubscriptionManagement
*/
